import { Loader2, Rocket } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import type { Release } from '../shared/types'
import { usePublishRelease } from './api'
import { MAX_NOTES, VERSION_RE } from './rules'

export function PublishReleaseModal({ release, onClose }: { release: Release; onClose: () => void }) {
  const { t } = useTranslation()
  const publish = usePublishRelease()
  const [version, setVersion] = useState('')
  const [notes, setNotes] = useState('')
  const [security, setSecurity] = useState(false)
  const [minPanel, setMinPanel] = useState('')
  const versionOk = VERSION_RE.test(version.trim())
  const minPanelOk = minPanel.trim() === '' || VERSION_RE.test(minPanel.trim())

  const submit = async () => {
    try {
      await publish.mutateAsync({
        id: release.id,
        version: version.trim(),
        notes: notes.trim(),
        security,
        min_panel_version: minPanel.trim(),
      })
      toast.success(t('updates.publish.done', { version: version.trim() }))
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!publish.isPending}
      icon={<Rocket />}
      title={t('updates.publish.title')}
      description={t('updates.publish.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!versionOk || !minPanelOk || publish.isPending}>
            {publish.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('updates.publish.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="bg-muted/40 rounded-lg border p-3 text-xs">
          <div className="truncate font-mono">{release.image_ref}</div>
          <div className="text-muted-foreground truncate font-mono">{release.image_digest}</div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pub-version">{t('updates.publish.version')}</Label>
          <Input
            id="pub-version"
            placeholder="1.5.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            aria-invalid={!!version && !versionOk}
          />
          {version && !versionOk ? (
            <p className="text-destructive text-xs">{t('updates.publish.versionInvalid')}</p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="pub-notes">{t('updates.publish.notes')}</Label>
          <Textarea
            id="pub-notes"
            rows={6}
            maxLength={MAX_NOTES}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pub-min-panel">{t('updates.publish.minPanel')}</Label>
          <Input
            id="pub-min-panel"
            placeholder="1.4.0"
            value={minPanel}
            onChange={(e) => setMinPanel(e.target.value)}
            aria-invalid={!minPanelOk}
          />
          <p className="text-muted-foreground text-xs">{t('updates.publish.minPanelHint')}</p>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={security}
            onChange={(e) => setSecurity(e.target.checked)}
          />
          <span>
            {t('updates.publish.security')}
            <span className="text-muted-foreground block text-xs">{t('updates.publish.securityHint')}</span>
          </span>
        </label>
        <p className="text-muted-foreground text-xs">{t('updates.publish.immutable')}</p>
      </div>
    </Modal>
  )
}
